import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { TaskRecord, TaskCadence } from '@/types/assets';
import { useAssetsStore } from '@/stores/assetsStore';
import { toast } from 'sonner';
import dayjs from 'dayjs';

const taskSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().optional(),
  cadence: z.enum(['once', 'daily', 'weekly', 'monthly', 'quarterly', 'yearly', 'custom_rrule']),
  rrule: z.string().optional(),
  owner: z.string().optional(),
  due_date: z.string().optional(),
  tags: z.string().optional(),
});

type TaskFormData = z.infer<typeof taskSchema>;

interface TaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  nodeId: string;
  task?: TaskRecord;
}

export function TaskDialog({ open, onOpenChange, nodeId, task }: TaskDialogProps) {
  const { addTask, updateTask } = useAssetsStore();
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<TaskFormData>({
    resolver: zodResolver(taskSchema),
    defaultValues: task ? {
      title: task.title,
      description: task.description,
      cadence: task.cadence,
      rrule: task.rrule,
      owner: task.owner,
      due_date: dayjs(task.due_next).format('YYYY-MM-DD'),
      tags: task.tags?.join(', '),
    } : {
      cadence: 'monthly',
      due_date: dayjs().add(1, 'month').format('YYYY-MM-DD'),
    },
  });

  const watchedCadence = watch('cadence');

  const onSubmit = (data: TaskFormData) => {
    const dueNext = data.due_date 
      ? dayjs(data.due_date).toISOString()
      : getNextDueDate(data.cadence);

    const tags = data.tags 
      ? data.tags.split(',').map(tag => tag.trim()).filter(Boolean)
      : [];

    if (task) {
      updateTask(task.id, {
        title: data.title,
        description: data.description,
        cadence: data.cadence,
        rrule: data.rrule,
        owner: data.owner,
        due_next: dueNext,
        tags,
      });
      toast.success('Task updated successfully');
    } else {
      addTask({
        nodeId,
        title: data.title,
        description: data.description,
        cadence: data.cadence,
        rrule: data.rrule,
        owner: data.owner,
        due_next: dueNext,
        status: 'Scheduled',
        tags,
      });
      toast.success('Task created successfully');
    }
    
    reset();
    onOpenChange(false);
  };

  const getNextDueDate = (cadence: TaskCadence): string => {
    const now = dayjs();
    switch (cadence) {
      case 'daily':
        return now.add(1, 'day').toISOString();
      case 'weekly':
        return now.add(1, 'week').toISOString();
      case 'monthly':
        return now.add(1, 'month').toISOString();
      case 'quarterly':
        return now.add(3, 'month').toISOString();
      case 'yearly':
        return now.add(1, 'year').toISOString();
      default:
        return now.add(1, 'month').toISOString();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{task ? 'Edit Task' : 'Create Recurring Task'}</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label htmlFor="title">Task Title *</Label>
            <Input
              id="title"
              {...register('title')}
              placeholder="Enter task title"
            />
            {errors.title && (
              <p className="text-sm text-destructive">{errors.title.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              {...register('description')}
              placeholder="Describe the task"
              className="min-h-[80px]"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="cadence">Cadence *</Label>
              <Select
                value={watchedCadence}
                onValueChange={(value) => setValue('cadence', value as TaskCadence)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="once">Once</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                  <SelectItem value="custom_rrule">Custom (RRULE)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="due_date">Next Due Date</Label>
              <Input
                id="due_date"
                type="date"
                {...register('due_date')}
              />
            </div>
          </div>

          {watchedCadence === 'custom_rrule' && (
            <div>
              <Label htmlFor="rrule">RRULE</Label>
              <Input
                id="rrule"
                {...register('rrule')}
                placeholder="FREQ=MONTHLY;INTERVAL=1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Enter a valid RRULE string for custom recurrence patterns
              </p>
            </div>
          )}

          <div>
            <Label htmlFor="owner">Owner</Label>
            <Input
              id="owner"
              {...register('owner')}
              placeholder="Person or team responsible"
            />
          </div>

          <div>
            <Label htmlFor="tags">Tags</Label>
            <Input
              id="tags"
              {...register('tags')}
              placeholder="backup, patch, configuration (comma-separated)"
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit">
              {task ? 'Update Task' : 'Create Task'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}